export enum DefaultEnum {
    defaultVisibleTrain = 7,
    defaultIncrementTrainLoadMore = 2,
    defaultDatesShow = 7,
    defaultInterval = 90,
    HorizontalCalendarDayScroll = 60
}
export enum PaymentTypeEnum {
    SalesPointePaymentCard = "SALES_POINT_PAYMENT_TYPE.CARD",
    SalesPointePaymentCash = "SALES_POINT_PAYMENT_TYPE.CASH"
}

export enum PassengerCardEnum {
    Student = "Student",
    ReducedMobility = "POD",
    Infant = "INF"
}

export enum PassengerCardTypeEnum {
    ReductionCard = "REDUCTION_CARD"
}

export enum PassengerPrmNeedTypeEnum {
    Wheelchair = "WHEELCHAIR"
}

export enum HonorificEnum {
    Dr = "Dr",
    Mr = "Mr",
    Mrs = "Mrs",
    Ms = "Ms"
}


export enum LocationEnum {
    origin = "origin",
    destination = "destination"
}

export enum ProductColorEnum {
    STANDARD = "#33C2A2",
    PREMIUM = "#143D8C"
}

export const ServiceClassTypeEnum = {
    STANDARD: process.env.NEXT_PUBLIC_SERVICE_CLASS_STANDARD || "STANDARD",
    HIGH: process.env.NEXT_PUBLIC_SERVICE_CLASS_PREMIUM || "HIGH",
} as const;

// Wire-level travel-class values returned by the place-map and offer
// APIs (compartment-level `travelClass` field). FIRST corresponds to
// premium coaches, SECOND to standard. Kept as `as const` so callers
// can compare against the enum without giving up literal-type narrowing.
export const TravelClassEnum = {
    FIRST: "FIRST",
    SECOND: "SECOND",
} as const;

// Wire-level status returned on each `place` in the place-map response
// (`PlaceMap.ts` declares `status: string`). Only the two values
// currently compared against in the codebase are enumerated — extend
// here as new comparisons are introduced.
export const PlaceMapStatusEnum = {
    ALLOCATED: "ALLOCATED",
    RESTRICTED: "RESTRICTED",
} as const;

// Wire-level entries in `place.placeProperties[]` returned by the
// place-map response. Used to discover accessibility / amenity flags
// on individual places. Add new keys here as new comparisons land.
export const PlacePropertyEnum = {
    WHEELCHAIR_AREA: "WHEELCHAIR_AREA",
} as const;

export const SHUTTLE_PRODUCT_CODE = (process.env.NEXT_PUBLIC_SHUTTLE_PRODUCT_CODE || "SHTL").toUpperCase();

/**
 * The shuttle-code predicate for the call sites that use it. NOT yet universal:
 * direct `SHUTTLE_PRODUCT_CODE` comparisons remain elsewhere
 * (UpgradePremiumHelper, BookingAtom, validationUtil, booking.ts) still using
 * `===` / `.includes`. Converting those is follow-up work; until then the
 * codebase is deliberately split, so do not assume this is the only spelling.
 *
 * Reach for this rather than hand-rolling the comparison. GetBookingToRightDataAction
 * once carried 16 inline copies reading `.some(p => p.summary.includes("shuttle"))`
 * across ALL of an offer's products; that is how booking 5HQ2PP's train came to
 * be labelled "Shuttle" (see `isShuttleOffer` below). Copies drift, and they
 * drift one call site at a time.
 *
 * Four spellings used to coexist (`===`, `.includes`, `.startsWith` twice),
 * which is how a money-adjacent check drifts. PREFIX is the safe unifier:
 * - offer `products[].code` is a bare "SHTL" in captured bookings, so `===`
 *   and prefix agree there;
 * - ancillary `type` IS route-suffixed ("SHTL_ADNEC-MBZ", "SHTL_X"), where
 *   `===` silently fails.
 * A false negative hides a shuttle the passenger already paid for, so bias to
 * the looser match. No non-shuttle code begins with the configured prefix.
 *
 * Note `.includes` is deliberately NOT the unifier: it matched unrelated codes
 * such as "NIGHTSHIFT".
 */
let warnedOnShortShuttleCode = false;
export const isShuttleProductCode = (code?: string | null): boolean => {
    // Prefix matching makes a misconfigured env dangerous in a way `===` was
    // not: a short NEXT_PUBLIC_SHUTTLE_PRODUCT_CODE (say "S") would classify
    // STV/SFC as shuttles, and that feeds trainType in GetBookingAction. Refuse
    // to match on an implausibly short prefix rather than mislabel trains.
    // Refusing to match is the safer half — but it disables shuttle detection at
    // THESE sites while the ~45 unconverted comparisons keep mislabelling, which
    // is a split brain. So fail LOUDLY rather than silently.
    if (SHUTTLE_PRODUCT_CODE.length < 3) {
        if (!warnedOnShortShuttleCode) {
            warnedOnShortShuttleCode = true;
            // eslint-disable-next-line no-console
            console.error(
                `[config] NEXT_PUBLIC_SHUTTLE_PRODUCT_CODE="${SHUTTLE_PRODUCT_CODE}" is too short for ` +
                `prefix matching; shuttle detection is disabled at isShuttleProductCode call sites.`
            );
        }
        return false;
    }
    return String(code || "").toUpperCase().startsWith(SHUTTLE_PRODUCT_CODE);
};

/**
 * Is this BOOKED OFFER a shuttle offer?
 *
 * Decided on the offer's ADMISSION product — never on `products[0]`, and never
 * on "any product carries a shuttle code".
 *
 * An offer's `products` array mixes what the passenger TRAVELS ON with what
 * they bought alongside it, and the backend does not order it. Booking 5HQ2PP
 * (preprod, 2026-08-07) carries its TRAIN offer as
 *   [SHTL-MBZ-REEM (ANCILLARY_ITEM), PRF (ADMISSION_POINT2POINT), PREMRES]
 * — a "Shuttle From/To Reem Mall" ancillary sitting at index 0 of a train
 * offer. `isShuttleProductCode` matches it correctly (it IS a shuttle product,
 * and the prefix match is what lets route-suffixed codes resolve at all); what
 * was wrong was asking that question of the offer's first product and calling
 * the answer the offer's type.
 *
 * The damage was booking-wide, not cosmetic: every non-shuttle trip dropped out
 * of `filteredTrips`, so the outbound trip fell back to a shuttle trip, the
 * one-way read as a round trip, `trainOffers` emptied, and the cancel summary
 * lost its "1x Adult" row and itemised "3x Shuttle ticket" — the train's own
 * AED 150 admission counted as a shuttle ticket.
 *
 * The admission is what the offer IS; ancillaries are what came with it.
 *
 * Implemented by EXCLUDING ancillaries rather than by selecting admissions.
 * Both fix 5HQ2PP, but "admissions if any, else every product" still misreads a
 * train offer whose admission product is typed anything other than
 * ADMISSION_POINT2POINT while a shuttle ancillary rides along: no admission
 * matches, so it falls back to every product and the ancillary wins again — the
 * original bug, one shape over. Excluding ANCILLARY_ITEM has no such hole and
 * still resolves untyped payloads, where `type` is undefined and so passes.
 *
 * These are OBSERVED wire values — `product.type` is not documented in either
 * swagger (docs/retailer.json, docs/transcity.json). ADMISSION_POINT2POINT is
 * the spelling ~14 existing call sites already rely on (booking.ts:130,
 * validationUtil.ts:57, UpgradePremiumHelper.ts:54, …) and ANCILLARY_ITEM is
 * what 5HQ2PP's Reem Mall product carries — consistent with the codebase, but
 * do not go looking for either in the spec.
 */
export const isShuttleOffer = (offer?: {
    products?: ({ code?: string | null; type?: string | null } | null)[] | null;
} | null): boolean =>
    (offer?.products || [])
        .filter(Boolean)
        .filter((product) => product?.type !== ProductTypeEnum.ANCILLARY_ITEM)
        .some((product) => isShuttleProductCode(product?.code));

export const NOL_PRODUCT_CODE = (process.env.NEXT_PUBLIC_NOL_PRODUCT_CODE || "NOL").toUpperCase();
/**
 * Product tag marking a NOL pass on the /fmlm-offers response.
 *
 * This is the ONLY reliable discriminator: /fmlm-offers returns non-NOL
 * first/last-mile products alongside the NOL passes on the same stop (observed
 * on INT: "NOL Day Pass — Gold, All Zones" tagged NOL_PASS at AED 9 listed
 * beside an untagged "FMLM travel pass" at AED 25). Product codes cannot be
 * matched instead — they are per-variant and arbitrary ("TEST4_gold").
 */
export const NOL_PASS_PRODUCT_TAG = (process.env.NEXT_PUBLIC_NOL_PASS_TAG || "NOL_PASS").toUpperCase();
export const NOL_VALIDITY_DAYS = Number(process.env.NEXT_PUBLIC_NOL_VALIDITY_DAYS) || 90;
/**
 * Attribute code the backend stamps on NOL-enabled stations (INT: Dubai Station
 * / PDW). This is the PRIMARY signal for NOL eligibility — see
 * StopManager.stopHasNolAttribute. Reading the attribute means a second NOL
 * station needs no client release.
 */
export const NOL_STOP_ATTRIBUTE_CODE = "NOL";

// FALLBACK ONLY — INT's /stops does not stamp "NOL" on any station yet, and it
// is also empty for the window before the stops dataset has loaded. Without
// this the Nol row silently vanishes from a leg /fmlm-offers is actively
// selling passes for. Comma-separated env override.
// ponytail: delete this and its use in nolSide once the backend stamps the
// attribute — that is the real fix, and it fixes web and mobile at once.
export const DUBAI_STOP_REFS = new Set(
  (process.env.NEXT_PUBLIC_DUBAI_STOP_REFS || "PDW")
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean),
);


// Wire-format product codes returned by the backend. Member names mirror the
// codes themselves so the enum makes no semantic claim about the tier — that
// classification lives in PRODUCT_TIER below, which is the single source of
// truth for fare-tier logic (fall-up chains, exchangeability buckets, etc.).
export enum ProductCodeEnum {
    // Admission (point-to-point)
    SFC = "SFC",
    SFF = "SFF",
    STV = "STV",
    PFP = "PFP",
    PFF = "PFF",
    PRV = "PRV",
    PRS = "PRS",

    // Group seat (non-refundable duo / quad)
    STDNFDUO = "STDNFDUO",
    STDNFQUAD = "STDNFQUAD",
    PREMNFDUO = "PREMNFDUO",
    PREMNFQUAD = "PREMNFQUAD",
}

// Fare tiers for tier-aware logic (quick exchange uses it to keep group seats
// and unknown codes out). Decoupled from ProductCodeEnum so the wire format and the
// business taxonomy can evolve independently.
export enum ProductTier {
    STANDARD_SAVER = "STANDARD_SAVER",
    STANDARD_VALUE = "STANDARD_VALUE",
    PREMIUM_SAVER = "PREMIUM_SAVER",
    PREMIUM_VALUE = "PREMIUM_VALUE",
    GROUP_SEAT = "GROUP_SEAT",
}

// Classification of every wire code into a fare tier. Exhaustive by type
// (`Record<ProductCodeEnum, ProductTier>`), so a new code added to
// ProductCodeEnum without a tier assignment is a compile error.
//
// Note: PFF's user-facing copy says "Premium Flex Plus" — it's classified
// here as PREMIUM_VALUE despite historical references to it as a "Saver"
// product. PRS is the actual Premium Saver code per the latest backend
// product taxonomy.
export const PRODUCT_TIER: Record<ProductCodeEnum, ProductTier> = {
    [ProductCodeEnum.SFC]: ProductTier.STANDARD_SAVER,
    [ProductCodeEnum.SFF]: ProductTier.STANDARD_VALUE,
    [ProductCodeEnum.STV]: ProductTier.STANDARD_VALUE,
    [ProductCodeEnum.PFP]: ProductTier.PREMIUM_VALUE,
    [ProductCodeEnum.PFF]: ProductTier.PREMIUM_VALUE,
    [ProductCodeEnum.PRV]: ProductTier.PREMIUM_VALUE,
    [ProductCodeEnum.PRS]: ProductTier.PREMIUM_SAVER,
    [ProductCodeEnum.STDNFDUO]: ProductTier.GROUP_SEAT,
    [ProductCodeEnum.STDNFQUAD]: ProductTier.GROUP_SEAT,
    [ProductCodeEnum.PREMNFDUO]: ProductTier.GROUP_SEAT,
    [ProductCodeEnum.PREMNFQUAD]: ProductTier.GROUP_SEAT,
};

// Backend `product.type` discriminator. Other product types exist on the
// backend (e.g. ancillary, shuttle) — add members here as code paths need them.
export enum ProductTypeEnum {
    ADMISSION_POINT2POINT = "ADMISSION_POINT2POINT",
    /** Bought alongside the travel right, never the travel right itself. */
    ANCILLARY_ITEM = "ANCILLARY_ITEM",
}

export enum TrainTypeEnum {
    outbound = "outbound",
    inbound = "inbound"
}

export enum TripTypeEnum {
    OUT_BOUND = "OUT_BOUND",
    IN_BOUND = "IN_BOUND"
}

export enum ConfirmationSlugEnum {
    cancel_trip = "cancel-trip",
    manage_seating = "manage-seating",
    change_date = "change-date",
    quick_exchange = "quick-exchange"
}

export enum TripDirectionTypeEnum {
    OUTBOUND = "OUTBOUND",
    INBOUND = "INBOUND"
}

export enum ContactDetailsEnum {
    EMAIL = "support@etihadrail.ae",
    PHONE = "800 RAIL (800 7245)",
    WHATSAPP = "+971 2 807 8888",
}

export enum ExpiryCacheTime {
    HOUR24 = 24 * 60 * 60 * 1000
}

export enum passegerTypeEnum {
    infants = "infants",
    children = "children"
}

export enum ticketClassTypeEnum {
    standardValue = "standardValue",
    standardPro = "standardPro",
    premium = "premium"
}
export enum AddonsCategoryEnum {
    CAR_PARKING = "Car Park",
    MEAL = "Meal",
    LOUNGE = "Lounge"
}

export enum AncillaryStatusEnum {
    PREBOOKED = "PREBOOKED",
    CONFIRMED = "CONFIRMED",
    FULFILLED = "FULFILLED",
    REFUNDED = "REFUNDED",
    EXCHANGE_ONGOING = "EXCHANGE_ONGOING",
}

// `bookingPartSummaries[].type` — mirrors AbstractBookingPartObjectType in
// docs/retailer.json. A booking's parts carry independent statuses per type,
// so anything comparing against the admission-derived booking status must
// filter to ADMISSION first.
export enum BookingPartTypeEnum {
    ADMISSION = "Admission",
    ANCILLARY = "Ancillary",
    RESERVATION = "Reservation",
}

export enum AdmissionStatusEnum {
    PREBOOKED = "PREBOOKED",
    FULFILLED = "FULFILLED",
    REFUNDED = "REFUNDED",
    EXCHANGE_ONGOING = "EXCHANGE_ONGOING",
    EXCHANGED = "EXCHANGED",
    ON_HOLD = "ON_HOLD",
    RELEASED = "RELEASED",
}

// Derived status written to `bookingConfirmStatusAtom` by
// GetBookingDetailStatusAction. FULFILLED/REFUNDED share string values with
// AdmissionStatusEnum (the action derives from admissions) but PAST is a
// time-based status with no admission analogue.
export enum BookingStatusEnum {
    PAST = "PAST",
    FULFILLED = "FULFILLED",
    REFUNDED = "REFUNDED",
}

// Top-level outcome on every `IApiResponse` returned by the backend
// wrapper. Matches the literal union in `IApiResponse.status` —
// keep these in lockstep.
export enum ApiResponseStatusEnum {
    OK = "ok",
    KO = "ko",
}

// Business error codes returned in `IApiResponse.errorCode` on a `ko`
// response. Used by the per-trip modification probe (BookingOptions
// + GetExchangeOfferByBooking) to distinguish a definitive "this leg
// is non-modifiable" verdict from transient failures (5xx, validation,
// unrelated business errors), which must be ignored so a future probe
// can retry instead of caching a false negative for the session.
export enum BookingExchangeErrorCode {
    BOOKING_MODIFICATION_NOT_ALLOWED = "BOOKING_MODIFICATION_NOT_ALLOWED",
}

export enum trainType {
    shuttle = "shuttle"
}

/**
 * Public-transport mode tag carried on every leg's
 * `timedLeg.service.mode.ptMode`. The backend emits "BUS" for shuttle legs
 * and "TRAIN" for train legs.
 */
export enum PtModeEnum {
    BUS = "BUS",
    TRAIN = "TRAIN",
}

export enum enumOfferPassType {
    monthlyPass = "monthlyPass",
    travelPass = "travelPass"
};

export enum enumPassType {
    TRAVEL_PASS = "TRAVEL_PASS",
}
